"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.drawingParameterToRuntimeSettings = void 0;
/**
 * Converts platform-level drawing parameter settings ({@link IDrawingParameterSettings})
 * to the runtime settings format ({@link SettingsOptional}) consumed by the drawing tools engine.
 *
 * This centralizes the bridging logic that was previously duplicated in consumer code
 * (e.g. the AppBuilder SDK's `useDrawingTools` hook).
 *
 * @param paramSettings - The platform-level parameter settings.
 * @param resolvedRestrictions - Pre-resolved restriction properties, keyed by token.
 *   Use the restriction resolution helpers from the interaction/restriction packages.
 * @param initialPoints - Optional initial point data (overrides `paramSettings.geometry.points`).
 * @returns A {@link SettingsOptional} object ready for {@link createDrawingTools}.
 */
const drawingParameterToRuntimeSettings = (paramSettings, resolvedRestrictions, initialPoints) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const behavior = paramSettings.behavior;
    const geometry = paramSettings.geometry;
    const options = (_a = paramSettings.general) === null || _a === void 0 ? void 0 : _a.options;
    return {
        controls: paramSettings.controls,
        general: {
            autoStart: (_b = behavior === null || behavior === void 0 ? void 0 : behavior.autoStart) !== null && _b !== void 0 ? _b : true,
            autoUpdate: (_c = behavior === null || behavior === void 0 ? void 0 : behavior.autoUpdate) !== null && _c !== void 0 ? _c : false,
            closeOnUpdate: (_d = behavior === null || behavior === void 0 ? void 0 : behavior.closeOnUpdate) !== null && _d !== void 0 ? _d : false,
            displayUnit: (_e = behavior === null || behavior === void 0 ? void 0 : behavior.displayUnit) !== null && _e !== void 0 ? _e : "",
            enableTranslation: (_f = behavior === null || behavior === void 0 ? void 0 : behavior.enableTranslation) !== null && _f !== void 0 ? _f : true,
            enableInsertion: (_g = behavior === null || behavior === void 0 ? void 0 : behavior.enableInsertion) !== null && _g !== void 0 ? _g : true,
            enableDeletion: (_h = behavior === null || behavior === void 0 ? void 0 : behavior.enableDeletion) !== null && _h !== void 0 ? _h : true,
            enableSelection: (_j = behavior === null || behavior === void 0 ? void 0 : behavior.enableSelection) !== null && _j !== void 0 ? _j : true,
        },
        geometry: {
            points: (_k = initialPoints !== null && initialPoints !== void 0 ? initialPoints : geometry === null || geometry === void 0 ? void 0 : geometry.points) !== null && _k !== void 0 ? _k : [],
            mode: (_l = geometry === null || geometry === void 0 ? void 0 : geometry.mode) !== null && _l !== void 0 ? _l : "lines",
            minPoints: geometry === null || geometry === void 0 ? void 0 : geometry.minPoints,
            maxPoints: geometry === null || geometry === void 0 ? void 0 : geometry.maxPoints,
            strictMinMaxPoints: (_m = geometry === null || geometry === void 0 ? void 0 : geometry.strictMinMaxPoints) !== null && _m !== void 0 ? _m : true,
            close: (_o = geometry === null || geometry === void 0 ? void 0 : geometry.close) !== null && _o !== void 0 ? _o : true,
            autoClose: (_p = geometry === null || geometry === void 0 ? void 0 : geometry.autoClose) !== null && _p !== void 0 ? _p : true,
            weightedAdjacency: geometry === null || geometry === void 0 ? void 0 : geometry.weightedAdjacency,
            disabledPoints: geometry === null || geometry === void 0 ? void 0 : geometry.disabledPoints,
            constraints: geometry === null || geometry === void 0 ? void 0 : geometry.constraints,
        },
        keyBindings: paramSettings.keyBindings,
        restrictions: applySnapDefaults(resolvedRestrictions, options),
        visualization: paramSettings.visualization,
    };
};
exports.drawingParameterToRuntimeSettings = drawingParameterToRuntimeSettings;
/**
 * Applies global snap defaults from general.options to geometry restrictions
 * that don't already have per-restriction values set.
 */
const applySnapDefaults = (restrictions, options) => {
    if (!restrictions || !options)
        return restrictions;
    for (const key of Object.keys(restrictions)) {
        const r = restrictions[key];
        if (!r || !("nodes" in r))
            continue; // only geometry restrictions have nodes
        if (options.snapToVertices !== undefined &&
            r.snapToVertices === undefined)
            r.snapToVertices = options.snapToVertices;
        if (options.snapToEdges !== undefined && r.snapToEdges === undefined)
            r.snapToEdges = options.snapToEdges;
        if (options.snapToFaces !== undefined && r.snapToFaces === undefined)
            r.snapToFaces = options.snapToFaces;
    }
    return restrictions;
};
//# sourceMappingURL=DrawingParameterSettingsConverter.js.map